<template>
  <div ref="crudTableFitRoot" :data-testid="getTestId('panel-product-crud-container')">
    <div class="panel-product-table-scroll">
      <v-data-table
        :headers="headers"
        :items="panelProducts"
        class="elevation-1 panel-product-data-table"
        :data-testid="getTestId('panel-product-data-table')"
        hide-default-footer
        hide-details="auto"
        :loading="apiLoading"
        :loading-text="$t('table.loading')"
        :custom-sort="customSort"
        :options.sync="tableOptions"
        @update:options="changedOptions"
      >
      <template slot="top">
        <v-toolbar flat class="solvit-crud-table-toolbar" :data-testid="getTestId('panel-product-toolbar')">
          <v-toolbar-title style="align-items: center; display: flex">
            <v-icon left>
              mdi-solar-panel
            </v-icon>{{ $t('panelProductCrud.title') }}
          </v-toolbar-title>
          <v-divider class="mx-4" inset vertical />
          <v-spacer />
          <v-btn-toggle
            v-model="columnMode"
            class="mr-2 flex-shrink-0"
            dense
            mandatory
            borderless
          >
            <v-btn
              small
              value="compact"
              outlined
              class="solvit-crud-outline-btn"
              :data-testid="getTestId('panel-product-columns-compact')"
            >
              {{ $t('panelProductCrud.columnsCompact') }}
            </v-btn>
            <v-btn
              small
              value="all"
              outlined
              class="solvit-crud-outline-btn"
              :data-testid="getTestId('panel-product-columns-all')"
            >
              {{ $t('panelProductCrud.columnsAll') }}
            </v-btn>
          </v-btn-toggle>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('panelProductCrud.searchLabel')"
            single-line
            hide-details="auto"
            class="panel-product-search-field"
            :data-testid="getTestId('panel-product-search-input')"
          />
          <v-spacer />
          <v-btn
            outlined
            class="mb-2 solvit-crud-outline-btn solvit-crud-outline-btn--primary"
            :data-testid="getTestId('panel-product-add-button')"
            @click="openAddDialog"
          >
            <v-icon size="medium">
              mdi-plus
            </v-icon>
            <span class="ml-1">{{ $t('common.addNew') }}</span>
          </v-btn>
        </v-toolbar>
      </template>
      <template slot="item.actions" slot-scope="{ item }">
        <FixDeleteBtn
          :key="'delete-btn-' + Date.now()"
          :item="item"
          :data-testid="getTestId('panel-product-action-buttons')"
          @editItem="editItem"
          @deleteItem="deleteItem"
        />
      </template>
      <template slot="item.productImagePath" slot-scope="{ item }">
        <div
          class="panel-image-container"
          :style="{ minHeight: panelImageSize + 'px' }"
        >
          <v-img
            v-if="item.productImagePath"
            :src="item.productImagePath"
            :alt="item.modelName ? $t('panelProductCrud.imageAltWithName', { name: item.modelName }) : $t('panelProductCrud.imageAltDefault')"
            :width="panelImageSize"
            :height="panelImageSize"
            contain
            class="panel-image"
          />
          <v-icon
            v-else
            :size="panelImageSize"
            color="secondary"
            class="panel-image"
          >
            mdi-solar-panel
          </v-icon>
        </div>
      </template>
      <template slot="item.dimensions" slot-scope="{ item }">
        <span>{{ formatDimensions(item.width, item.height, item.thickness) }}</span>
      </template>
      <template slot="item.maxPowerOutput" slot-scope="{ item }">
        <span>{{ item.maxPowerOutput ? `${item.maxPowerOutput}W` : '-' }}</span>
      </template>

      <template slot="item.maxVoltage" slot-scope="{ item }">
        <span>{{ item.maxVoltage ? `${item.maxVoltage}V` : '-' }}</span>
      </template>
      <template slot="item.maxCurrent" slot-scope="{ item }">
        <span>{{ item.maxCurrent ? `${item.maxCurrent}A` : '-' }}</span>
      </template>
      <template slot="item.openCircuitVoltage" slot-scope="{ item }">
        <span>{{ item.openCircuitVoltage ? `${item.openCircuitVoltage}V` : '-' }}</span>
      </template>
      <template slot="item.shortCircuitCurrent" slot-scope="{ item }">
        <span>{{ item.shortCircuitCurrent ? `${item.shortCircuitCurrent}A` : '-' }}</span>
      </template>
      <template slot="item.weight" slot-scope="{ item }">
        <span>{{ item.weight ? `${item.weight}kg` : '-' }}</span>
      </template>
      <template slot="item.thickness" slot-scope="{ item }">
        <span>{{ item.thickness ? `${item.thickness}mm` : '-' }}</span>
      </template>
      <template slot="item.warrantyPeriodYears" slot-scope="{ item }">
        <span>{{ item.warrantyPeriodYears ? $t('panelProductCrud.warrantyYears', { years: item.warrantyPeriodYears }) : '-' }}</span>
      </template>
      <template slot="item.numberOfCells" slot-scope="{ item }">
        <span>{{ item.numberOfCells || '-' }}</span>
      </template>
      <template slot="item.cellEfficiency" slot-scope="{ item }">
        <span>{{ item.cellEfficiency ? `${item.cellEfficiency}%` : '-' }}</span>
      </template>
      <template slot="item.operatingTemp" slot-scope="{ item }">
        <span v-if="item.operatingTempMin && item.operatingTempMax">
          {{ item.operatingTempMin }}°C ~ {{ item.operatingTempMax }}°C
        </span>
        <span v-else-if="item.operatingTempMin">
          {{ item.operatingTempMin }}°C ~
        </span>
        <span v-else-if="item.operatingTempMax">
          ~ {{ item.operatingTempMax }}°C
        </span>
        <span v-else>-</span>
      </template>
      <template slot="item.ipRating" slot-scope="{ item }">
        <span>{{ item.ipRating || '-' }}</span>
      </template>
      <template slot="item.panelProductCellType" slot-scope="{ item }">
        <span>{{ getCellTypeLabel(item.panelProductCellType) }}</span>
      </template>
      <template slot="no-data">
        <v-btn outlined class="solvit-crud-outline-btn" :data-testid="getTestId('panel-product-refresh-button')" @click="initialize">
          {{ $t('common.refresh') }}
        </v-btn>
      </template>
    </v-data-table>
    </div>
    <div class="text-center pt-2">
      <v-pagination
        v-model="page"
        :length="pageCount"
        :total-visible="10"
        next-icon="mdi-chevron-right"
        prev-icon="mdi-chevron-left"
        :data-testid="getTestId('panel-product-pagination')"
      />
    </div>
    <PanelProductEditDialog
      v-if="addDialog"
      :key="addDialogKey"
      :dialog="addDialog"
      :item="defaultItem"
      :cell-types="cellTypes"
      :data-testid="getTestId('panel-product-add-dialog')"
      @close="closeAddDialog"
      @success="handleAddSuccess"
    />
    <PanelProductEditDialog
      v-if="editDialog"
      :key="editDialogKey"
      :dialog="editDialog"
      :cell-types="cellTypes"
      :item="selectedItem"
      :data-testid="getTestId('panel-product-edit-dialog')"
      @close="closeEditDialog"
      @success="handleEditSuccess"
    />
  </div>
</template>

<script>
import Api from '../../apis/Api';
import FixDeleteBtn from '../button/FixDeleteBtn.vue';
import PanelProductEditDialog from '../../components/dialog/PanelProductEditDialog.vue';
import { getTestId } from '@/utils/testUtils';
import { PANEL_PRODUCT_TABLE_HEADERS, PANEL_PRODUCT_COMPACT_COLUMN_VALUES, PANEL_PRODUCT_IMAGE_SIZE, DEFAULT_PANEL_PRODUCT } from '@/constants/panelProduct';
import { formatDimensions, getCellTypeLabel } from '@/utils/panelProductUtils';
import { 
  createDefaultTableOptions, 
  buildSortParam, 
  buildPaginationParams, 
  buildApiUrl, 
  transformApiResponse 
} from '../../utils/tableUtils';
import { transformToPanelProductTableItems } from '../../constants/panelProductTableItem';
import { crudTablePageSizeFitMixin } from '@/ui/mixins/crudTablePageSizeFitMixin';

export default {
  mixins: [crudTablePageSizeFitMixin],
  components: {
    FixDeleteBtn,
    PanelProductEditDialog
  },
  data() {
    return {
    // 기본은 핵심 컬럼 — 좁은 화면에서 가로 붕괴 방지. 전체는 토글 + 가로 스크롤
    columnMode: 'compact',
    // 이미지 셀 — 실측 전 fallback. compact/all에 따라 갱신
    crudTableFitOptions: {
      rowHeight: PANEL_PRODUCT_IMAGE_SIZE.compact + 16,
      min: 3,
      max: 20
    },
    // tableUtils를 사용한 기본 테이블 옵션 생성 (created에서 viewport fit로 덮어씀)
    tableOptions: createDefaultTableOptions(['id'], [true], 7),
    
    valid: true,
    requiredRule: (v) => !!v || this.$t('common.required'),
    requiredSelectRule: (v) => v.id > 0 || this.$t('common.required'),
    // 기존 변수들은 유지 (하위 호환성)
    sortBy: ['id'],
    sortDesc: [true],
    itemsPerPage: 7,
    pageCount: 0,
    paging: true,
    page: 1,
    domain: 'panelproduct',
    addDialog: false,
    addDialogKey: Date.now(),
    editDialog: false,
    editDialogKey: Date.now(),
    search: '',
    panelProducts: [],
    cellTypes: [],
    editedIndex: -1,
    editedItem: {
      seq: null,
      modelName: '',
      maker: '',
      maxPowerOutput: null,
      width: null,
      height: null,
      panelProductCellType: null
    },
    defaultItem: DEFAULT_PANEL_PRODUCT,
    apiLoading: true,
    selectedItem: null
    };
  },
  computed: {
    formTitle() {
      return this.editedIndex === -1 ? this.$t('common.addNew') : this.$t('common.edit');
    },
    isCompactColumns() {
      return this.columnMode !== 'all';
    },
    panelImageSize() {
      return this.isCompactColumns
        ? PANEL_PRODUCT_IMAGE_SIZE.compact
        : PANEL_PRODUCT_IMAGE_SIZE.full;
    },
    headers() {
      void this.$i18n.locale;
      const mapped = PANEL_PRODUCT_TABLE_HEADERS.map((h) => ({
        ...h,
        text: this.$t(`panelProductCrud.columns.${h.value}`)
      }));
      if (!this.isCompactColumns) {
        return mapped;
      }
      return mapped.filter((h) => PANEL_PRODUCT_COMPACT_COLUMN_VALUES.includes(h.value));
    }
  },
  watch: {
    columnMode() {
      this.crudTableFitOptions = {
        ...this.crudTableFitOptions,
        rowHeight: this.panelImageSize + 16
      };
      this.$nextTick(() => {
        if (typeof this.calibrateCrudPageSizeFromDom === 'function') {
          this.calibrateCrudPageSizeFromDom({ refetch: true });
        }
      });
    },
    search(val) {
      // 검색어 변경 시 즉시 실행
      this.searchPanelProductData(val, 1, this.tableOptions.sortBy, this.tableOptions.sortDesc);
    },
    page(val) {
      if (this.isCrudTableFitProgrammaticUpdate()) {
        return;
      }
      this.page = val;
      // 페이지 변경은 즉시 실행
      this.searchPanelProductData(this.search, val, this.tableOptions.sortBy, this.tableOptions.sortDesc);
    }
  },
  created() {
    //changedOptions에서 실행
    // this.initialize();
  },
  mounted() {
    // 초기 정렬 옵션을 명시적으로 설정
    this.tableOptions.sortBy = ['id'];
    this.tableOptions.sortDesc = [true];
    
    // options.sync를 사용할 때는 Vuetify가 자동으로 초기화 이벤트를 발생시킴
    // 따라서 mounted에서 별도로 initialize 호출할 필요 없음
  },
  methods: {
    getTestId(testId) {
      return getTestId(testId);
    },
    customSort(items, _index, _isDesc) {
      return items;
    },
    /**
     * tableUtils를 사용한 정렬 파라미터 변환
     * @param {Array} sortBy - 정렬할 필드들
     * @returns {Array} API 요청용 정렬 필드 배열
     */
    changeSortByArrayToApiParam(sortBy) {
      // tableUtils의 buildSortParam을 사용하여 정렬 파라미터 생성
      if (sortBy && sortBy.length > 0) {
        const sortableFields = {
          'modelName': 'modelName',
          'maker': 'maker',
          'maxPowerOutput': 'maxPowerOutput',
          'panelProductCellType': 'panelProductCellType',
          'id': 'id'
        };
        
        // buildSortParam은 전체 정렬 파라미터를 반환하므로 필드만 추출
        const sortParam = buildSortParam(sortBy, this.tableOptions.sortDesc, sortableFields);
        const [field] = sortParam.split(',');
        return [field];
      }
      return ["id"];
    },
    changedOptions(val) {
      
      // tableOptions는 이미 자동으로 업데이트됨
      // 별도로 변경할 필요 없음
      
      // 초기 로드 시에도 데이터를 가져오기 위해 searchPanelProductData 호출
      this.searchPanelProductData(
        this.search, 
        this.page, 
        this.tableOptions.sortBy, 
        this.tableOptions.sortDesc
      );
    },
    /**
     * 실무 표준: 기존 Api.getApiResource를 사용한 안전한 API 호출
     * @param {string} keyword - 검색 키워드
     * @param {number} page - 페이지 번호
     * @param {Array} sortBy - 정렬 필드
     * @param {Array} sortDesc - 정렬 방향
     */
    searchPanelProductData(keyword, page, sortBy, sortDesc) {
      const request = this.beginCrudTableRequest();
      this.apiLoading = true;
      
      // tableUtils를 사용하여 정렬 파라미터 생성
      const sortableFields = {
        'modelName': 'modelName',
        'maker': 'maker',
        'maxPowerOutput': 'maxPowerOutput',
        'panelProductCellType': 'panelProductCellType',
        'id': 'id'
      };
      
      const sortParam = buildSortParam(sortBy || this.tableOptions.sortBy, sortDesc || this.tableOptions.sortDesc, sortableFields);
      const pagination = buildPaginationParams(page || this.page, this.itemsPerPage);
      
      Promise.all([
        // 패널 제품 데이터 조회 - 가독성 개선: API 호출 바로 위에서 params 구성
        Api.getApiResource(`/${this.domain}`, {
          size: pagination.size,
          page: pagination.page,
          sort: sortParam,
          ...(keyword && keyword.trim() && { keyword: keyword.trim() })
        }, { signal: request.signal }),
        // 셀 타입 목록 조회
        Api.read('/panelproduct/cellTypes', { signal: request.signal })
      ])
        .then(([panelProductRes, cellTypeRes]) => {
          if (!this.isCrudTableRequestCurrent(request.token)) return;

          
          // API 응답 구조 확인 후 직접 처리
          if (panelProductRes.data?.content?.content && Array.isArray(panelProductRes.data.content.content)) {
            // 기존 API 응답 구조 사용
            this.panelProducts = panelProductRes.data.content.content;
            this.pageCount = panelProductRes.data.content.page?.totalPages || 0;
            this.page = (panelProductRes.data.content.page?.number || 0) + 1;
            
          } else {
            // tableUtils를 사용하여 응답 데이터 변환 (대안)
            try {
              const transformedData = transformApiResponse(panelProductRes, (items) => {

                return items; // 패널 제품은 그대로 사용
              });
              

              
              // 변환된 데이터로 상태 업데이트
              this.panelProducts = transformedData.items;
              this.pageCount = transformedData.pageCount;
              this.page = transformedData.page;
              
            } catch (error) {
              console.error("tableUtils 변환 에러:", error);
              // 에러 발생 시 빈 배열로 설정
              this.panelProducts = [];
              this.pageCount = 0;
              this.page = 1;
            }
          }
          
          // 원본 객체 형태로 유지 (PanelProductEditDialog에서 처리)
          this.cellTypes = cellTypeRes?.data?.content || {};
          
        })
        .catch((error) => {
          if (!this.isCrudTableRequestCurrent(request.token)) return;
          console.error("API 호출 에러:", error);
          // 에러 발생 시 빈 배열로 설정
          this.panelProducts = [];
          this.pageCount = 0;
          this.page = 1;
        })
        .finally(() => {
          if (!this.isCrudTableRequestCurrent(request.token)) return;
          this.apiLoading = false;
          this.finishCrudTableRequest(request.token);
        });
    },
    initialize() {
      //changedOptions에서 실행함
      this.searchPanelProductData();
    },
    editItem(item) {

      this.selectedItem = Object.assign({}, item);
      this.editDialog = true;
      this.editDialogKey = Date.now();
    },
    deleteItem(item) {
      const index = this.panelProducts.indexOf(item);
      confirm(this.$t('common.confirmDeleteSelected')) &&
        Api.delete(`/${this.domain}/${item.id}`).then(() => {
          this.panelProducts.splice(index, 1);
          this.initialize();
        });
    },
    closeEditDialog() {
      this.editDialog = false;
      this.selectedItem = null;
    },
    handleEditSuccess() {
      this.closeEditDialog();
      this.paging = true;
      this.searchPanelProductData();
    },
    openAddDialog() {
      this.addDialog = true;
      this.addDialogKey = Date.now();
    },
    closeAddDialog() {
      this.addDialog = false;
    },
    handleAddSuccess() {
      this.closeAddDialog();
      this.initialize();
    },
    fetchPanelProducts() {
      this.initialize();
    },

    formatDimensions,
    getCellTypeLabel
  }
};
</script>

<style scoped>
.v-dialog__content {
  max-height: 100%;
}
.v-dialog:not(.v-dialog--fullscreen) {
  max-height: 100%;
}
.custom-padding {
  padding: 8px 12px !important;
}

.required::after {
  content: "*";
  color: red;
  margin-left: 4px;
}

.panel-product-table-scroll {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
}

.panel-product-search-field {
  max-width: 280px;
}

/* 이미지 로드 전에도 행 높이 안정 → 디스플레이별 page size 실측이 흔들리지 않음 */
.panel-image-container {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
